<template>
    <Spinner v-if="displaySpinner" />
    <InfoMessage
        title="Shape your business context"
        storageKey="welcome-message-domain"
        :forceShow="!hasAnyContent"
        showToggle
    >
        <p>
            Domains organize related products and automation processes. Think of
            a domain as a small business within your organization that provides
            value to the outside world, whether to other internal domains or
            external clients.
        </p>
        <p v-if="products.length === 0">
            Take the first step by creating a product or service. This defines
            what your domain brings to the table.
        </p>
    </InfoMessage>

    <Page>
        <template #eyebrow>
            <Eyebrow icon="pi pi-cloud">Domain</Eyebrow>
        </template>
        <template #title>
            <EditableTitle v-model="domain" />
        </template>
        <template #controls>
            <LastChangedInfo
                v-if="domain?.identity"
                :entityId="domain.identity"
                :entityType="EntityType.DOMAIN"
            />
            <Button
                v-if="description && !description.content && isEditing"
                v-tooltip.bottom="{
                    value: 'Will generate a first draft of the description based on the context.',
                    showDelay: 100,
                }"
                severity="secondary"
                icon="pi pi-sparkles"
                label="Generate first draft"
                @click="generateDomainDescription"
            />
        </template>

        <Section class="description" editable>
            <TextAppArtefact
                v-if="description"
                v-model="description"
                :isEditing="isEditing"
            />
        </Section>

        <Section editable ref="productsSectionRef">
            <ProductsList :isEditing="isEditing" :products="products" />
        </Section>

        <Section editable ref="definitionsSectionRef">
            <EntitiesList :isEditing="isEditing" :entities="entities" />
        </Section>

        <Section editable>
            <DomainTermsList
                :isEditing="isEditing"
                :domainTerms="domainTerms"
            />
        </Section>

        <Section editable ref="rolesSectionRef">
            <RolesList :isEditing="isEditing" :roles="roles" />
        </Section>

        <Section editable>
            <Documents
                v-if="domain"
                entityType="domain"
                :entity="domain"
                :isEditing="isEditing"
                :artefacts="artefacts"
            />
        </Section>
    </Page>

    <HintPopover
        v-if="currentHintStep"
        :current-step="currentHintStep"
        :is-last-step="isLastHintStep"
        @next="nextHintStep"
        @dismiss-all="dismissAllHints"
    />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import Documents from "@/components/Documents.vue";
import DomainTermsList from "@/components/DomainTerms/DomainTermsList.vue";
import EditableTitle from "@/components/EditableTitle.vue";
import Eyebrow from "@/components/Eyebrow.vue";
import InfoMessage from "@/components/InfoMessage.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import ProductsList from "@/components/Products/ProductsList.vue";
import RolesList from "@/components/Roles/RolesList.vue";
import Section from "@/components/Section.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import EntitiesList from "@/components/entities/EntitiesList.vue";
import HintPopover from "@/components/hints/HintPopover.vue";
import { useDomainManager } from "@/composables/domain-manager";
import { useEditManager } from "@/composables/edit-manager";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";
import { usePageHints } from "@/composables/use-page-hints";
import { useArtefactsManager } from "@/lib/artefacts/user";
import Spinner from "@/shared/Spinner.vue";
import { useBusinessEntitiesManager } from "@/stores/business-entities-store";
import { useDomainTermsManager } from "@/stores/domain-terms-store";
import { EntityType } from "@/stores/entity-store";
import { useProductsManager } from "@/stores/products-store";
import { useRolesManager } from "@/stores/roles-store";

const productsSectionRef = ref<HTMLElement | null>(null);
const definitionsSectionRef = ref<HTMLElement | null>(null);
const rolesSectionRef = ref<HTMLElement | null>(null);

const {
    currentStep: currentHintStep,
    isLastStep: isLastHintStep,
    nextStep: nextHintStep,
    dismissAll: dismissAllHints,
} = usePageHints([
    {
        id: "domain-add-product",
        targetRef: productsSectionRef,
        title: "Define your offerings",
        content:
            "Products and services are the value your domain brings to the world — what others can look up, request, or rely on.",
    },
    {
        id: "domain-definitions",
        targetRef: definitionsSectionRef,
        title: "Speak a common language",
        content:
            "Capture the key concepts and data objects your domain relies on. A shared vocabulary keeps everyone aligned.",
    },
    {
        id: "domain-roles",
        targetRef: rolesSectionRef,
        title: "Assign responsibilities",
        content:
            "Roles bundle responsibilities together. Assign them to people, and use them to drive the processes in your domain.",
    },
]);

const { artefacts } = useArtefactsManager();
const { products } = useProductsManager();
const { entities } = useBusinessEntitiesManager();
const { domainTerms } = useDomainTermsManager();
const { roles } = useRolesManager();
const { isEditing } = useEditManager();
const { domain, isFetching, description } = useDomainManager();
const { generateContent } = useGenerateDescriptionDialog();

const displaySpinner = computed(() => isFetching.value);

const generateDomainDescription = async () => {
    if (!domain.value) {
        return;
    }
    await generateContent(
        domain.value.identity,
        EntityType.DOMAIN,
        description.value?.updateContent.bind(description.value)
    );
};

const hasAnyContent = computed(() => {
    return Boolean(
        products.value?.length > 0 ||
        description.value?.content ||
        entities.value?.length > 0 ||
        artefacts.value?.length > 0 ||
        (roles.value && roles.value.length > 1)
    );
});
</script>
